/**
 * Plan 13-02 — TanStack Query v5 hooks for the Logsheet Checker feature.
 *
 * Mirrors the `useLogsheetMakerQueries` pattern from Plan 12-03:
 *  - Typed query/mutation hooks over the `logsheet-checker.ts` service layer.
 *  - `staleTime: 30_000` on list queries; `staleTime: 5_000` on detail query.
 *  - Mutations invalidate the relevant query keys on success.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/api/error';
import {
  approveRequest,
  bulkApproveRequest,
  getCheckerHistoryRequest,
  getPendingDetailRequest,
  getPendingRequest,
  getRecentEntriesRequest,
  rejectRequest,
  uploadCheckerDocumentRequest,
} from '@/services/logsheet-checker';
import type {
  ApproveRequest,
  BulkApproveRequest,
  BulkApproveResult,
  CheckerDetail,
  HistoryItem,
  HistoryListQuery,
  PendingItem,
  PendingListQuery,
  RecentEntry,
  RejectRequest,
} from '@/types/logsheet-checker';
import type { LogsheetDocument } from '@/types/logsheets-maker';
import type { PaginatedResult } from '@/types/roles';

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

/** Paginated queue of Submitted entries for the current checker. */
export function useCheckerPendingQuery(query: PendingListQuery = {}) {
  return useQuery<PaginatedResult<PendingItem>, ApiError>({
    queryKey: ['checker', 'pending', query],
    queryFn: () => getPendingRequest(query),
    staleTime: 30_000,
  });
}

/** Full detail for a single pending entry. */
export function useCheckerDetailQuery(id: number) {
  return useQuery<CheckerDetail, ApiError>({
    queryKey: ['checker', 'detail', id],
    queryFn: () => getPendingDetailRequest(id),
    enabled: Number.isFinite(id) && id > 0,
    staleTime: 5_000,
  });
}

/** Last-5 historical entries for the same machine (comparison panel). */
export function useRecentEntriesQuery(id: number) {
  return useQuery<readonly RecentEntry[], ApiError>({
    queryKey: ['checker', 'recent', id],
    queryFn: () => getRecentEntriesRequest(id),
    enabled: Number.isFinite(id) && id > 0,
    staleTime: 30_000,
  });
}

/** Checker's own action history (Checked + Rejected). */
export function useCheckerHistoryQuery(query: HistoryListQuery = {}) {
  return useQuery<PaginatedResult<HistoryItem>, ApiError>({
    queryKey: ['checker', 'history', query],
    queryFn: () => getCheckerHistoryRequest(query),
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

interface ApproveVars {
  readonly id: number;
  readonly body: ApproveRequest;
}

/** Approve a single pending entry (Submitted → Checked). */
export function useApproveMutation() {
  const qc = useQueryClient();
  return useMutation<CheckerDetail, ApiError, ApproveVars>({
    mutationFn: ({ id, body }) => approveRequest(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['checker', 'pending'] });
      void qc.invalidateQueries({ queryKey: ['checker', 'history'] });
      // Checked entries are the approver queue's input — refresh approver too.
      void qc.invalidateQueries({ queryKey: ['approver', 'pending'] });
      // REVIEW FF-030: PendingForMeWidget checker+approver counts both move.
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

interface RejectVars {
  readonly id: number;
  readonly body: RejectRequest;
}

/** Reject a pending entry with reason + remarks. */
export function useRejectMutation() {
  const qc = useQueryClient();
  return useMutation<CheckerDetail, ApiError, RejectVars>({
    mutationFn: ({ id, body }) => rejectRequest(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['checker', 'pending'] });
      void qc.invalidateQueries({ queryKey: ['checker', 'history'] });
      // REVIEW FF-030: rejected entry returns to maker — checker count drops.
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

interface UploadDocumentVars {
  readonly file: File;
  readonly onProgress?: (percent: number) => void;
}

/** Upload a document to a pending entry. `entryId` must be provided at hook creation time. */
export function useUploadDocumentMutation(entryId: number) {
  const qc = useQueryClient();
  return useMutation<LogsheetDocument, ApiError, UploadDocumentVars>({
    mutationFn: ({ file, onProgress }) => uploadCheckerDocumentRequest(entryId, file, onProgress),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['checker', 'detail', entryId] });
    },
  });
}

/** Bulk-approve up to 100 pending entries. */
export function useBulkApproveMutation() {
  const qc = useQueryClient();
  return useMutation<BulkApproveResult, ApiError, BulkApproveRequest>({
    mutationFn: (body) => bulkApproveRequest(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['checker', 'pending'] });
      void qc.invalidateQueries({ queryKey: ['checker', 'history'] });
      void qc.invalidateQueries({ queryKey: ['approver', 'pending'] });
      // REVIEW FF-030: bulk-approve flips many entries — dashboard counts must
      // refetch.
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}
